import { useMemo, useState } from "react";
import { Loader2, Search, RefreshCw, CheckCircle2, Clock, XCircle, AlertTriangle } from "lucide-react";
import {
  useChapterExamApplicationsQuery,
  useChapterExamApplicationQuery,
  useChapterEndorseExamApplicationMutation,
  useChapterDeclineExamApplicationMutation,
} from "../../api/examsApi";

/**
 * Exam applications from the chairman's or secretary's own chapter, waiting to
 * be endorsed.
 *
 * PAID ones only — the fee is what makes an application real, and the server
 * filters on it rather than trusting anything sent from here. Endorsing is what
 * sends a submitted application to the secretariat; endorsing one the candidate
 * has not submitted yet is allowed and simply waits for them
 * (ExamApplicationWorkflow::maybeStartReview decides, not this screen).
 */

const STATUS_CLS: Record<string, string> = {
  paid: "bg-teal-100 text-teal-700 border-teal-200",
  submitted: "bg-sky-100 text-sky-700 border-sky-200",
  under_review: "bg-sky-100 text-sky-900 border-sky-300",
  with_committee: "bg-indigo-100 text-indigo-700 border-indigo-200",
  approved: "bg-green-100 text-green-700 border-green-200",
  rejected: "bg-red-100 text-red-700 border-red-200",
  graded: "bg-blue-100 text-blue-700 border-blue-200",
  completed: "bg-green-100 text-green-700 border-green-200",
};

const TABS = ["awaiting", "all", "endorsed", "rejected"] as const;
type Tab = (typeof TABS)[number];

const TAB_LABEL: Record<Tab, string> = {
  awaiting: "Awaiting your endorsement",
  all: "All paid",
  endorsed: "Endorsed",
  rejected: "Declined",
};

function fmtDate(iso?: string | null) {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? String(iso)
    : d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

function StatusBadge({ status }: { status?: string }) {
  const s = String(status ?? "").toLowerCase();
  return (
    <span className={`inline-flex items-center px-2 py-1 text-xs border rounded-full ${STATUS_CLS[s] ?? "bg-gray-100 text-gray-600 border-gray-200"}`}>
      {s.replace(/_/g, " ") || "—"}
    </span>
  );
}

/* ── The application itself ──────────────────────────────────────────────── */

function Field({ label, value }: { label: string; value: any }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-widest text-gray-400">{label}</div>
      <div className="text-sm text-gray-800 break-words">
        {value === 0 || value ? value : "—"}
      </div>
    </div>
  );
}

function Rows({ title, rows, cols }: { title: string; rows: any[]; cols: [string, (r: any) => any][] }) {
  if (!rows?.length) return null;

  return (
    <div>
      <h4 className="text-[11px] font-black uppercase tracking-widest text-gray-500 mb-2">{title}</h4>
      <div className="border rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] font-bold uppercase tracking-widest text-gray-400 border-b">
              {cols.map(([label]) => <th key={label} className="px-3 py-2">{label}</th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {rows.map((r: any, i: number) => (
              <tr key={r.id ?? i}>
                {cols.map(([label, get]) => (
                  <td key={label} className="px-3 py-2 text-gray-700">{get(r) ?? "—"}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * What the chapter is being asked to put its name behind.
 *
 * An officer endorsing off a name and an exam code is signing for somebody they
 * have not read about. Everything the candidate declared is here, read-only —
 * the chapter neither fills this in nor corrects it.
 */
function DetailsModal({ id, onClose }: { id: string; onClose: () => void }) {
  const { data, isLoading, isError } = useChapterExamApplicationQuery(id);

  const app = data?.application ?? data;
  const sections = data?.form_sections;
  const form = app?.form;

  // Full-bleed on a phone, a card on a desktop: a sheet this long has to
  // scroll inside itself rather than run off the side of the screen.
  return (
    <div className="fixed inset-0 bg-black/40 flex items-start justify-center p-0 sm:p-4 z-50 overflow-y-auto">
      <div className="bg-white w-full max-w-3xl min-h-full sm:min-h-0 sm:my-8 sm:rounded-2xl">
        <div className="flex items-start justify-between gap-4 p-4 sm:p-5 border-b sticky top-0 bg-white sm:static">
          <div className="min-w-0">
            <h2 className="font-semibold text-gray-900 break-words">{app?.user?.name ?? "Application"}</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {[app?.exam?.code, app?.exam?.title, app?.diet?.title].filter(Boolean).join(" · ")}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 text-sm font-semibold shrink-0"
          >
            Close
          </button>
        </div>

        <div className="p-4 sm:p-5 space-y-6">
          {isLoading ? (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading the application…
            </div>
          ) : isError || !app ? (
            <p className="text-sm text-rose-700">Could not load this application.</p>
          ) : (
            <>
              <div>
                <h4 className="text-[11px] font-black uppercase tracking-widest text-gray-500 mb-2">
                  Candidate
                </h4>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <Field label="Name" value={app.user?.name} />
                  <Field label="Membership no." value={app.user?.member?.membership_no} />
                  <Field label="Phone" value={app.user?.member?.phone ?? form?.phone} />
                  <Field label="Email" value={app.user?.email ?? form?.email} />
                  <Field label="Grade" value={form?.membership_grade} />
                  <Field label="Chapter" value={app.chapter?.name} />
                </div>
              </div>

              <div>
                <h4 className="text-[11px] font-black uppercase tracking-widest text-gray-500 mb-2">
                  This application
                </h4>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <Field label="Exam" value={[app.exam?.code, app.exam?.title].filter(Boolean).join(" — ")} />
                  <Field label="Diet" value={app.diet?.title} />
                  <Field label="Status" value={String(app.status ?? "").replace(/_/g, " ")} />
                  <Field label="Paid on" value={fmtDate(app.paid_at)} />
                  <Field label="Centre" value={app.center?.name} />
                  <Field label="Exam no." value={app.exam_no} />
                  <Field label="Graduation year" value={app.graduation_year} />
                  <Field
                    label="Endorsed"
                    value={app.chapter_endorsed_at
                      ? `${fmtDate(app.chapter_endorsed_at)}${app.chapter_endorsed_by?.name ? ` by ${app.chapter_endorsed_by.name}` : ""}`
                      : null}
                  />
                  <Field label="Endorsement comment" value={app.chapter_endorse_comment} />
                </div>
              </div>

              <Rows
                title="Registered courses"
                rows={app.registered_courses ?? []}
                cols={[
                  ["Code", (r) => r.course?.code],
                  ["Course", (r) => r.course?.name ?? r.course?.title],
                ]}
              />

              <Rows
                title="Papers"
                rows={app.papers ?? []}
                cols={[
                  ["Code", (r) => r.paper?.code],
                  ["Paper", (r) => r.paper?.title],
                ]}
              />

              {sections ? (
                <>
                  <Rows
                    title="O'level results"
                    rows={sections.olevels ?? []}
                    cols={[
                      ["Subject", (r) => r.subject],
                      ["Grade", (r) => String(r.grade ?? "").toUpperCase()],
                      ["Exam", (r) => String(r.exam_type ?? "").toUpperCase()],
                      ["Year", (r) => r.exam_year],
                    ]}
                  />
                  <Rows
                    title="Academic qualifications"
                    rows={sections.educations ?? []}
                    cols={[
                      ["Institution", (r) => r.institution],
                      ["Qualification", (r) => r.qualification],
                      ["Completed", (r) => String(r.completed_on ?? "").slice(0, 4)],
                    ]}
                  />
                  <Rows
                    title="Professional experience"
                    rows={sections.experiences ?? []}
                    cols={[
                      ["Employer", (r) => r.employer_name],
                      ["Position", (r) => r.position],
                      ["From", (r) => String(r.start_date ?? "").slice(0, 10)],
                      ["To", (r) => String(r.end_date ?? "").slice(0, 10) || "Present"],
                    ]}
                  />
                  <Rows
                    title="Other professional institutions"
                    rows={sections.memberships ?? []}
                    cols={[
                      ["Institution", (r) => r.institution],
                      ["Grade", (r) => r.current_grade],
                      ["Year", (r) => r.year_obtained],
                    ]}
                  />

                  {sections.employer ? (
                    <div>
                      <h4 className="text-[11px] font-black uppercase tracking-widest text-gray-500 mb-2">
                        Present employer
                      </h4>
                      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        <Field label="Employer" value={sections.employer.name} />
                        <Field label="Position" value={sections.employer.applicant_position} />
                        <Field label="Since" value={String(sections.employer.employment_date ?? "").slice(0, 10)} />
                      </div>
                    </div>
                  ) : null}

                  {sections.sponsor ? (
                    <div>
                      <h4 className="text-[11px] font-black uppercase tracking-widest text-gray-500 mb-2">
                        Sponsor
                      </h4>
                      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        <Field label="Sponsor" value={sections.sponsor.full_name} />
                        <Field label="Membership no." value={sections.sponsor.membership_no} />
                        <Field label="Grade" value={sections.sponsor.membership_grade} />
                      </div>
                    </div>
                  ) : null}
                </>
              ) : (
                <p className="text-sm text-gray-400">
                  This exam does not carry the NIQS application form, so there is nothing further
                  the candidate has declared.
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ChapterExamApplicationsPage() {
  const { data, isLoading, isFetching, refetch } = useChapterExamApplicationsQuery();
  const [endorse, { isLoading: endorsing }] = useChapterEndorseExamApplicationMutation();
  const [decline, { isLoading: declining }] = useChapterDeclineExamApplicationMutation();

  const [tab, setTab] = useState<Tab>("awaiting");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState<any>(null);      // the row being acted on
  const [viewing, setViewing] = useState<string | null>(null); // the row being read
  const [mode, setMode] = useState<"endorse" | "decline">("endorse");
  const [text, setText] = useState("");
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const rows: any[] = data?.data ?? [];
  const chapter = data?.chapter;
  const actingFor: any[] = data?.acting_for ?? [];

  const counts = useMemo(() => {
    const awaiting = rows.filter((r) => !r.chapter_endorsed_at && r.status !== "rejected").length;
    return {
      awaiting,
      all: rows.length,
      endorsed: rows.filter((r) => !!r.chapter_endorsed_at).length,
      rejected: rows.filter((r) => r.status === "rejected").length,
    };
  }, [rows]);

  const filtered = useMemo(() => {
    const nq = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (tab === "awaiting" && (r.chapter_endorsed_at || r.status === "rejected")) return false;
      if (tab === "endorsed" && !r.chapter_endorsed_at) return false;
      if (tab === "rejected" && r.status !== "rejected") return false;
      if (!nq) return true;
      return [r.user?.name, r.user?.email, r.user?.member?.membership_no, r.exam?.code, r.exam?.title, r.exam_no]
        .filter(Boolean).join(" ").toLowerCase().includes(nq);
    });
  }, [rows, tab, q]);

  const submit = async () => {
    setMsg(null);
    if (text.trim().length < 3) {
      setMsg({ ok: false, text: mode === "endorse" ? "Add a short comment with your endorsement." : "Say why you are declining." });
      return;
    }
    try {
      const res: any = mode === "endorse"
        ? await endorse({ id: open.id, comment: text.trim() }).unwrap()
        : await decline({ id: open.id, reason: text.trim() }).unwrap();
      setOpen(null);
      setText("");
      setMsg({ ok: true, text: res?.message ?? "Recorded." });
    } catch (e: any) {
      setMsg({ ok: false, text: e?.data?.message ?? "Could not record that." });
    }
  };

  if (isLoading) {
    return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;
  }

  return (
    <div className="w-full p-4 md:p-6 max-w-6xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Exam endorsements</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Paid exam applications from {chapter?.name ? `the ${chapter.name} chapter` : "your chapter"}. Your
            endorsement is what sends one to the secretariat.
            {actingFor.length > 0 && (
              <> You are also acting for {actingFor.map((c) => c.name).join(", ")}.</>
            )}
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border text-sm hover:bg-gray-50 shrink-0 self-start"
        >
          <RefreshCw className={`w-4 h-4 ${isFetching ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {msg && (
        <div className={`mb-3 p-3 rounded-lg border text-sm flex items-start gap-2 ${
          msg.ok ? "bg-green-50 border-green-200 text-green-800" : "bg-red-50 border-red-200 text-red-700"
        }`}>
          {!msg.ok && <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />} {msg.text}
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-3">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-3 py-1.5 rounded-lg border text-xs sm:text-sm whitespace-nowrap ${
              tab === t ? "bg-gray-900 text-white border-gray-900" : "bg-white hover:bg-gray-50"
            }`}
          >
            {TAB_LABEL[t]}
            <span className="ml-2 text-xs opacity-70">{counts[t]}</span>
          </button>
        ))}
      </div>

      <div className="relative mb-3">
        <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by candidate, membership number or exam…"
          className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-gray-900/10"
        />
      </div>

      {/* Six columns do not fit a phone, and squeezing them makes the two that
          matter — who, and what you are being asked to sign — unreadable. The
          same rows are cards below `md`; this table is the desktop view. */}
      <div className="hidden md:block bg-white border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-[11px] font-bold uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-4 py-3">Candidate</th>
              <th className="px-4 py-3">Exam</th>
              <th className="px-4 py-3">Paid</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Endorsement</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-gray-400">Nothing here.</td></tr>
            )}
            {filtered.map((r) => (
              <tr key={r.id} className="border-t">
                <td className="px-4 py-3">
                  <p className="text-gray-900">{r.user?.name ?? "—"}</p>
                  <p className="text-xs text-gray-400">
                    {[r.user?.member?.membership_no, r.user?.email].filter(Boolean).join(" · ")}
                  </p>
                </td>
                <td className="px-4 py-3">
                  <p className="text-gray-900">{r.exam?.code ?? "—"}</p>
                  <p className="text-xs text-gray-400">{r.diet?.title ?? ""}</p>
                </td>
                <td className="px-4 py-3 text-gray-600">{fmtDate(r.paid_at)}</td>
                <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                <td className="px-4 py-3">
                  {r.chapter_endorsed_at ? (
                    <span className="inline-flex items-center gap-1 text-xs text-green-700">
                      <CheckCircle2 className="w-3.5 h-3.5" /> {fmtDate(r.chapter_endorsed_at)}
                    </span>
                  ) : r.status === "rejected" ? (
                    <span className="inline-flex items-center gap-1 text-xs text-red-600">
                      <XCircle className="w-3.5 h-3.5" /> declined
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs text-amber-600">
                      <Clock className="w-3.5 h-3.5" /> awaiting you
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex gap-2 justify-end">
                    {/* Offered on EVERY row, endorsed or not: an officer asked
                        later why the chapter backed someone needs to be able to
                        read the application, not only before deciding. */}
                    <button
                      onClick={() => setViewing(String(r.id))}
                      className="px-3 py-1.5 rounded-lg border text-xs font-medium hover:bg-gray-50"
                    >
                      View
                    </button>
                    {!r.chapter_endorsed_at && r.status !== "rejected" && (
                      <>
                        <button
                          onClick={() => { setOpen(r); setMode("endorse"); setText(""); setMsg(null); }}
                          className="px-3 py-1.5 rounded-lg bg-green-600 text-white text-xs font-medium hover:bg-green-700"
                        >
                          Endorse
                        </button>
                        <button
                          onClick={() => { setOpen(r); setMode("decline"); setText(""); setMsg(null); }}
                          className="px-3 py-1.5 rounded-lg border border-red-200 text-red-600 text-xs font-medium hover:bg-red-50"
                        >
                          Decline
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* The same rows, for a phone. */}
      <div className="md:hidden space-y-3">
        {filtered.length === 0 && (
          <div className="bg-white border rounded-xl p-8 text-center text-gray-400 text-sm">
            Nothing here.
          </div>
        )}

        {filtered.map((r) => (
          <div key={r.id} className="bg-white border rounded-xl p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-semibold text-gray-900 break-words">{r.user?.name ?? "—"}</p>
                <p className="text-xs text-gray-400 break-words">
                  {[r.user?.member?.membership_no, r.user?.email].filter(Boolean).join(" · ")}
                </p>
              </div>
              <StatusBadge status={r.status} />
            </div>

            <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Exam</div>
                <div className="text-gray-800">{r.exam?.code ?? "—"}</div>
                <div className="text-gray-400">{r.diet?.title ?? ""}</div>
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Paid</div>
                <div className="text-gray-800">{fmtDate(r.paid_at)}</div>
              </div>
            </div>

            <div className="mt-3 pt-3 border-t flex flex-wrap items-center justify-between gap-2">
              {r.chapter_endorsed_at ? (
                <span className="inline-flex items-center gap-1 text-xs text-green-700">
                  <CheckCircle2 className="w-3.5 h-3.5" /> endorsed {fmtDate(r.chapter_endorsed_at)}
                </span>
              ) : r.status === "rejected" ? (
                <span className="inline-flex items-center gap-1 text-xs text-red-600">
                  <XCircle className="w-3.5 h-3.5" /> declined
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-xs text-amber-600">
                  <Clock className="w-3.5 h-3.5" /> awaiting you
                </span>
              )}

              <div className="flex gap-2 ml-auto">
                <button
                  onClick={() => setViewing(String(r.id))}
                  className="px-3 py-1.5 rounded-lg border text-xs font-medium hover:bg-gray-50"
                >
                  View
                </button>
                {!r.chapter_endorsed_at && r.status !== "rejected" && (
                  <>
                    <button
                      onClick={() => { setOpen(r); setMode("endorse"); setText(""); setMsg(null); }}
                      className="px-3 py-1.5 rounded-lg bg-green-600 text-white text-xs font-medium hover:bg-green-700"
                    >
                      Endorse
                    </button>
                    <button
                      onClick={() => { setOpen(r); setMode("decline"); setText(""); setMsg(null); }}
                      className="px-3 py-1.5 rounded-lg border border-red-200 text-red-600 text-xs font-medium hover:bg-red-50"
                    >
                      Decline
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {viewing && <DetailsModal id={viewing} onClose={() => setViewing(null)} />}

      {/* Endorse / decline */}
      {open && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-lg p-4 sm:p-5 my-auto">
            <h2 className="font-semibold text-gray-900">
              {mode === "endorse" ? "Endorse this application" : "Decline to endorse"}
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {open.user?.name} · {open.exam?.code ?? "exam"}
              {mode === "endorse" ? (
                open.status === "submitted"
                  ? " — endorsing sends it to the secretariat."
                  : " — the candidate has not submitted it yet, so it goes to the secretariat when they do."
              ) : " — this closes the application and tells the secretariat why."}
            </p>

            <textarea
              rows={4}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={mode === "endorse" ? "Your comment on this candidate…" : "Why you are not endorsing…"}
              className="mt-3 w-full px-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-gray-900/10"
            />

            {msg && !msg.ok && <p className="text-sm text-red-600 mt-2">{msg.text}</p>}

            <div className="mt-4 flex gap-2 justify-end">
              <button
                onClick={() => { setOpen(null); setText(""); }}
                className="px-4 py-2 rounded-lg border text-sm hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={submit}
                disabled={endorsing || declining}
                className={`px-4 py-2 rounded-lg text-white text-sm font-medium disabled:opacity-50 ${
                  mode === "endorse" ? "bg-green-600 hover:bg-green-700" : "bg-red-600 hover:bg-red-700"
                }`}
              >
                {(endorsing || declining) ? "Saving…" : mode === "endorse" ? "Endorse" : "Decline"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
